/**
 * Common React components, hooks, and UI logic for the signup management table.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCollection } from '@amzn/awsui-collection-hooks';
import Box from '@amzn/awsui-components-react/polaris/box';
import Select, { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { Alert, Button, PropertyFilterProps, SpaceBetween } from '@amzn/awsui-components-react';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import { FilterProperty } from '../attendance/wizard/tablePropertyFilter';
import { ColumnMap } from '../../hooks/tableColumns';
import Link from '@amzn/awsui-components-react/polaris/link';
import { Event, Shift } from '@amzn/red-velvet-api';
import { useTimezone } from '../../hooks/timezone';
import { useFeatures, featureIsEnabled } from '../../hooks/features';
import { useSearchParams } from 'react-router-dom';
import { useLocale } from '../../hooks/locale';
import { EVENT_CREATION } from '../../utils/constants';
import { downloadCsv } from '../../utils/csvDownload';
import { publishKatalMetric } from '../katalAnalytics';
import {
  AmazonSignup,
  buildSignupCsvFilename,
  buildSignupCsvHeaders,
  buildSignupCsvRows,
  getSignupBlockedReasons,
  getSignupVisibleQuestions,
  hasEventLevelBlock,
  resolveQuestionAnswers,
  shouldShowSignupLink,
  SignupBlockedReason,
  SignupStatus,
  TFunction,
  WAIVER_ACCEPTED_KEY,
  WAIVER_DECLINED_KEY,
} from '../../utils/signupUtils';

export const INITIAL_SIGNUP_PAGE_SIZE = 100;

export function DismissConfirmationAlert({
  visible,
  onStay,
  onLeave,
}: {
  visible: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const { t } = useTranslation('signup');
  if (!visible) return null;

  return (
    <Box margin={{ bottom: 's' }}>
      <Alert
        type="warning"
        dismissible
        onDismiss={onStay}
        header={t('bulkSignupModal.dismissAlert.title')}
        action={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={onStay}>{t('bulkSignupModal.dismissAlert.cancel')}</Button>
            <Button variant="primary" onClick={onLeave}>
              {t('bulkSignupModal.dismissAlert.confirm')}
            </Button>
          </SpaceBetween>
        }
      >
        {t('bulkSignupModal.dismissAlert.message')}
      </Alert>
    </Box>
  );
}

export function SignupBlockedPopover({
  blockedReasons,
  eventId,
  metricKey,
}: {
  blockedReasons: SignupBlockedReason[];
  eventId: string;
  metricKey?: string;
}) {
  const { t } = useTranslation('signup');
  const [searchParams] = useSearchParams();
  const urlFeatures = searchParams.get('features');
  if (blockedReasons.length === 0) return null;

  const showSignupLink = shouldShowSignupLink(blockedReasons);
  const eventDetailUrl = `/view-event?id=${eventId}&coordinator-action=signup${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const givehubManageSignupsUrl = `https://amazon.benevity.org/volunteer/${eventId}/manage/signups`;
  return (
    <Popover
      header={t('signupSheet.signupBlockedHeader')}
      content={
        <SpaceBetween size="s">
          <ul>
            {blockedReasons.map((reason) => (
              <li key={reason}>{t(`signupSheet.blockedReasons.${SignupBlockedReason[reason]}`)}</li>
            ))}
          </ul>
          {showSignupLink && (
            <>
              <Box>{t('signupSheet.signupBlockedFooter')}</Box>
              <SpaceBetween size="xs">
                <Link href={givehubManageSignupsUrl} external data-aci-analytics-name={metricKey}>
                  {t('signupSheet.manageInGivehub')}
                </Link>
                <Link href={eventDetailUrl}>{t('signupSheet.signupOnEventPage')}</Link>
              </SpaceBetween>
            </>
          )}
        </SpaceBetween>
      }
      triggerType="custom"
      size="large"
    >
      <Icon name="status-info" />
    </Popover>
  );
}

interface SignupStatusSelectProps {
  currentValue: SignupStatus;
  onStatusChange: (status: SignupStatus) => void;
  event: Event;
  shift: Shift;
  remainingSpots?: number;
}

export const SignupStatusSelect = ({
  currentValue,
  onStatusChange,
  event,
  shift,
  remainingSpots,
}: SignupStatusSelectProps) => {
  const { t } = useTranslation('signup');
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  const signupEvent = {
    ...event,
    questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
  };
  const disableReSignup =
    currentValue === SignupStatus.CANCEL &&
    getSignupBlockedReasons(signupEvent, shift, remainingSpots).length > 0;

  const STATUS_OPTIONS: SelectProps.Option[] = [
    { label: t('signupStatus.signedUp'), value: SignupStatus.SIGNUP, disabled: disableReSignup },
    { label: t('signupStatus.cancelled'), value: SignupStatus.CANCEL },
  ];

  return (
    <Select
      ariaLabel={t('signupSheet.selectStatus')}
      autoFocus
      expandToViewport
      options={STATUS_OPTIONS}
      selectedOption={STATUS_OPTIONS.find((option) => option.value === currentValue) ?? null}
      onChange={(event) => onStatusChange(event.detail.selectedOption.value as SignupStatus)}
    />
  );
};

export function useInlineEditDisabledReason(remainingSpots?: number) {
  const { t } = useTranslation('signup');
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const isEventCreationEnabled = featureIsEnabled(EVENT_CREATION, features, searchParams);
  return (signupStatus: SignupStatus, event: Event, shift: Shift): string | undefined => {
    if (signupStatus === SignupStatus.PENDING) return t('signupSheet.pending');
    const signupEvent = {
      ...event,
      questions: getSignupVisibleQuestions(event.questions, isEventCreationEnabled),
    };
    const reasons = getSignupBlockedReasons(signupEvent, shift, remainingSpots);
    const applicable =
      signupStatus === SignupStatus.SIGNUP
        ? reasons.filter((r) => hasEventLevelBlock([r]))
        : reasons;
    if (applicable.length === 0) return undefined;
    const header = t('signupSheet.signupBlockedHeader');
    const items = applicable.map(
      (r) => `• ${t(`signupSheet.blockedReasons.${SignupBlockedReason[r]}`)}`
    );
    return `${header}\n${items.join('\n')}`;
  };
}

interface DownloadSignupListButtonProps {
  event: Event;
  shift: Shift;
  signups: AmazonSignup[];
  isLoading: boolean;
  metricKey: string;
}

export function DownloadSignupListButton({
  event,
  shift,
  signups,
  isLoading,
  metricKey,
}: DownloadSignupListButtonProps) {
  const { t } = useTranslation('signup');
  const selectedTimezone = useTimezone();
  const locale = useLocale();

  const handleDownload = () => {
    const contentTimezone = event.timeZone || selectedTimezone;
    const rows = buildSignupCsvRows(signups, shift, event, contentTimezone, t, locale);
    const filename = buildSignupCsvFilename(
      event.eventId || '',
      shift.name || '',
      selectedTimezone
    );
    downloadCsv(filename, buildSignupCsvHeaders(event, t), rows);
    publishKatalMetric(metricKey, '');
  };

  return (
    <Button
      iconName="download"
      onClick={handleDownload}
      disabled={isLoading || signups.length === 0}
      data-aci-analytics-name={metricKey}
    >
      {t('signupSheet.downloadSignupList')}
    </Button>
  );
}

// Common filter properties shared between shift and event level
export const baseSignupFilterProperties: FilterProperty[] = [
  { name: 'name', operators: [':'] },
  { name: 'alias', operators: [':'] },
  { name: 'building', operators: [':'] },
  { name: 'managerLogin', operators: [':'] },
  { name: 'numberOfGuests', operators: ['=', '>', '<'] },
  { name: 'tShirtSize', operators: ['='] },
  { name: 'signupStatus', operators: ['='] },
];

export function useSignupFilterProperties(
  additionalProperties: FilterProperty[] = []
): PropertyFilterProps.FilteringProperty[] {
  const { t } = useTranslation('signup');

  const allProperties = [...baseSignupFilterProperties, ...additionalProperties];

  return allProperties.map((property) => ({
    key: property.name,
    propertyLabel: t('propertyFilter.properties.' + property.name),
    groupValuesLabel: t('propertyFilter.values.' + property.name),
    operators: property.operators,
    defaultOperator: property.operators ? property.operators[0] : undefined,
  }));
}

export function useBaseSignupColumnMap(): ColumnMap<AmazonSignup> {
  return {
    name: (item) => item.name,
    alias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
        {item.alias}
      </Link>
    ),
    building: (item) => item.building,
    managerAlias: (item) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
        {item.managerLogin}
      </Link>
    ),
    numberOfGuests: (item) => String(item.numberOfGuests),
    tShirtSize: (item) => item.tShirtSize,
    signupStatus: (item) => {
      switch (item.signupStatus) {
        case SignupStatus.SIGNUP:
          return <StatusIndicator type="success">{item.signupStatus}</StatusIndicator>;
        case SignupStatus.CANCEL:
          return <StatusIndicator type="error">{item.signupStatus}</StatusIndicator>;
        case SignupStatus.PENDING:
          return <StatusIndicator type="pending">{item.signupStatus}</StatusIndicator>;
        default:
          return item.signupStatus;
      }
    },
  };
}

export function useSignupTableCollection(
  signups: AmazonSignup[],
  filteringProperties: PropertyFilterProps.FilteringProperty[],
  isError: boolean,
  pageSize: number
) {
  const { t } = useTranslation('signup');

  return useCollection(signups, {
    filtering: {
      empty: (
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>{isError ? t('signupSheet.errorOccurred') : t('signupSheet.noneFound')}</b>
        </Box>
      ),
    },
    propertyFiltering: {
      filteringProperties,
    },
    pagination: { pageSize },
    sorting: {},
  });
}

export function buildCustomFieldColumnDefinitions(
  event?: Event,
  t?: TFunction
): TableProps.ColumnDefinition<AmazonSignup>[] {
  if (!event) return [];

  const columns: TableProps.ColumnDefinition<AmazonSignup>[] = [];

  if (event.waiver && event.waiver.acceptance !== 'none') {
    columns.push({
      id: 'waiver',
      header: t ? t('SignupSheetTableForShift.columns.waiver') : 'Waiver',
      cell: (item) =>
        item.waiverAccepted === true
          ? t
            ? t(WAIVER_ACCEPTED_KEY)
            : ''
          : item.waiverAccepted === false
            ? t
              ? t(WAIVER_DECLINED_KEY)
              : ''
            : '',
    });
  }

  if (event.questions && event.questions.length > 0) {
    const resolvedCache = new WeakMap<AmazonSignup, Map<string, string>>();

    const qaHeader = t
      ? t('SignupSheetTableForShift.columns.questionAnswers')
      : 'Signup questions & answers';
    const qaInfo = t
      ? t('SignupSheetTableForShift.questionAnswersInfo')
      : 'Only shows questions and answers collected through Volunteer Center. Responses collected or edited in GIVEhub will not appear here.';

    columns.push({
      id: 'questionAnswers',
      header: (
        <SpaceBetween direction="horizontal" size="xxs">
          <span>{qaHeader}</span>
          <Popover content={qaInfo} triggerType="custom" size="medium">
            <Icon name="status-info" />
          </Popover>
        </SpaceBetween>
      ),
      cell: (item) => {
        if (!resolvedCache.has(item)) {
          resolvedCache.set(item, resolveQuestionAnswers(item.questionAnswers, event.questions));
        }
        const resolved = resolvedCache.get(item) ?? new Map<string, string>();

        return (
          <Popover
            header={
              t
                ? t('SignupSheetTableForShift.columns.questionAnswers')
                : 'Signup questions & answers'
            }
            content={
              <SpaceBetween size="xs">
                {(event.questions ?? []).map((question) => (
                  <div key={question.id}>
                    <Box fontWeight="bold">{question.title}</Box>
                    <Box>{resolved.get(question.id ?? '') || '—'}</Box>
                  </div>
                ))}
              </SpaceBetween>
            }
            triggerType="text"
            size="large"
          >
            {t ? t('SignupSheetTableForShift.viewAnswers') : 'View answers'}
          </Popover>
        );
      },
    });
  }

  return columns;
}
